import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import type {
  SideNavigationItemData,
  SideNavigationItemProps,
  SideNavigationMode,
} from "./types";

const ACTIVE_ICON_COLOR_FALLBACK = "#80eaff";

const modeConfig = {
  expanded: {
    itemWidth: "100%",
    minHeight: 56,
    flexDirection: "row" as const,
    alignItems: "center" as const,
    justifyContent: "flex-start" as const,
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 12,
    iconSize: 24,
    labelFontSize: 16,
    labelLineHeight: 20,
    labelAlign: "left" as const,
    labelLines: 1,
  },
  collapsed: {
    itemWidth: 62,
    minHeight: 56,
    flexDirection: "column" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    paddingHorizontal: 4,
    paddingVertical: 8,
    gap: 4,
    iconSize: 24,
    labelFontSize: 12,
    labelLineHeight: 16,
    labelAlign: "center" as const,
    labelLines: 2,
  },
};

const getDisplayLabel = (
  item: SideNavigationItemData,
  mode: SideNavigationMode
) => (mode === "collapsed" ? item.shortLabel || item.label : item.label);

const renderIcon = (icon: React.ReactNode, size: number, color: string) => {
  if (!React.isValidElement(icon)) return icon;

  return React.cloneElement(
    icon as React.ReactElement<{ size?: number | string; color?: string }>,
    {
      size,
      color,
    }
  );
};

export const SideNavigationItem: React.FC<SideNavigationItemProps> = ({
  item,
  mode = "expanded",
  state = "default",
  isActive,
  onItemClick,
  linkComponent: LinkComponent,
  testID,
  themeMode,
  themeProductContext = "b2c",
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const config = modeConfig[mode];
  const resolvedState = isActive || state === "active" ? "active" : state;
  const isDisabled = Boolean(item.disabled);
  const backgroundColor =
    resolvedState === "active" || resolvedState === "hover"
      ? theme.colors.overlay.mono
      : "transparent";
  const borderColor =
    resolvedState === "active" ? theme.colors.border.secondary : "transparent";
  const labelColor = theme.colors.content.primary;
  const iconColor =
    resolvedState === "active"
      ? theme.colors.background.brand?.primary || ACTIVE_ICON_COLOR_FALLBACK
      : theme.colors.content.tertiary;

  const handlePress = () => {
    if (isDisabled) return;
    item.onPress?.();
    onItemClick?.(item);
  };

  const content = (
    <Box
      as={!LinkComponent && !isDisabled ? "button" : undefined}
      width={config.itemWidth}
      minHeight={config.minHeight}
      flexDirection={config.flexDirection}
      alignItems={config.alignItems}
      justifyContent={config.justifyContent}
      paddingHorizontal={config.paddingHorizontal}
      paddingVertical={config.paddingVertical}
      gap={config.gap}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderWidth={resolvedState === "active" ? 1 : 0}
      borderRadius={8}
      role={LinkComponent ? undefined : "button"}
      aria-label={item.accessibilityLabel || item.label}
      aria-current={resolvedState === "active" ? "page" : undefined}
      aria-disabled={isDisabled || undefined}
      disabled={isDisabled}
      onPress={!LinkComponent && !isDisabled ? handlePress : undefined}
      hoverStyle={
        resolvedState === "default"
          ? { backgroundColor: theme.colors.overlay.mono }
          : undefined
      }
      pressStyle={{ backgroundColor: theme.colors.overlay.mono }}
      testID={item.testID || `side-navigation-item-${item.key}`}
    >
      {item.icon && (
        <Box
          width={config.iconSize}
          height={config.iconSize}
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
        >
          {renderIcon(item.icon, config.iconSize, iconColor)}
        </Box>
      )}
      <Text
        color={labelColor}
        fontFamily={theme.fonts.body}
        fontSize={config.labelFontSize}
        lineHeight={config.labelLineHeight}
        fontWeight="400"
        textAlign={config.labelAlign}
        numberOfLines={config.labelLines}
      >
        {getDisplayLabel(item, mode)}
      </Text>
    </Box>
  );

  if (!LinkComponent) return content;

  return (
    <LinkComponent
      item={item}
      href={item.href}
      isActive={resolvedState === "active"}
      disabled={isDisabled}
      onPress={handlePress}
      testID={item.testID || `side-navigation-link-${item.key}`}
    >
      {content}
    </LinkComponent>
  );
};
